import styled from '@emotion/styled';
import { CSS } from '@dnd-kit/utilities';
import { useSortable } from '@dnd-kit/sortable';
import type { Palette } from '@mui/material';
import { IconButton } from '@mui/material';
import PolarisIcon from '@/components/_common/PolarisIcon';

type SortableItemProps = {
  index: number;
  code: string;
  languageLabel: JSX.Element | string | null;
  color: keyof Palette;
  onRemove: (code: string) => void;
};

const SortableLanguageItem: React.FC<SortableItemProps> = ({ index, code, languageLabel, color, onRemove }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: code });

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    cursor: isDragging ? 'grabbing' : 'grab'
  };

  return (
    <TrackItemDiv ref={setNodeRef} style={style} {...attributes} {...listeners} color={color}>
      <HandleSpan>
        <PolarisIcon icon="grip" />
      </HandleSpan>
      <TrackItemIndexStrong>Track {index + 1}: </TrackItemIndexStrong>
      <LangLabelSpan>{languageLabel}</LangLabelSpan>
      <StyledIconButton size="small" onClick={() => onRemove(code)}>
        <PolarisIcon icon="close" />
      </StyledIconButton>
    </TrackItemDiv>
  );
};

export default SortableLanguageItem;

const LangLabelSpan = styled.span`
  color: ${({ theme }) => theme.palette.text.primary};
`;

const HandleSpan = styled.span`
  font-size: 1.2rem;
  display: flex;
  align-items: center;
  margin-right: ${({ theme }) => theme.spacing(1)};
  opacity: 0.3;
`;

const TrackItemDiv = styled.div<{ color: keyof Palette }>`
  padding: ${({ theme }) => theme.spacing(1)};
  display: flex;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing(0.5)};
  border-radius: ${({ theme }) => theme.shape.borderRadius};

  & strong {
    opacity: 0.75;
    padding-right: ${({ theme }) => theme.spacing(1)};
    font-weight: inherit;
  }

  ${({ theme, color }) => {
    switch (color) {
      case 'primary':
        return `
        background: ${theme.palette.primary.main}1a;
        & strong {
          color: ${theme.palette.primary.main};
        }
      `;
      case 'secondary':
        return `
        background: ${theme.palette.secondary.main}1a;
        & strong {
          color: ${theme.palette.secondary.main};
        }
      `;
      case 'tertiary':
        return `
        background: ${theme.palette.tertiary.main}1a;
        & strong {
          color: ${theme.palette.tertiary.main};
        }
      `;
      case 'teal':
        return `
        background: var(--teal-light);
        & strong {
          color: var(--teal-main);
        }
      `;
      case 'default':
        return `
        background: ${theme.palette.background.default};
      `;
      default:
        return ``;
    }
  }}
`;

const TrackItemIndexStrong = styled.strong`
  white-space: nowrap;
`;

const StyledIconButton = styled(IconButton)`
  font-size: 1.2rem;
  margin-left: auto;
  opacity: 0.6;
`;
